import React, { useMemo, useCallback, useState } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable
} from 'material-react-table';
import propTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Eye, CircleX, Loader2 } from 'lucide-react';
import Button from 'tailwind-components/Button';
import FlagPill from 'tailwind-components/FlagPill/FlagPill';
import { getCompany } from 'helpers/getCompanyVariables';
import { isSelfAgent, getCannotOffboardSelfMessage } from 'helpers/agents';
import useHiddenMode, { maskValue } from 'hooks/useHiddenMode';
import useViewAs from 'hooks/useViewAs';
import { getViewAsDisabledReason } from 'helpers/viewAsAgentLink';
import { isMobileWebView, postMobileViewAsAgent } from 'helpers/mobileWebView';
import { isHoldRank } from './AgentRankTabs';
import {
  AGENT_STATUS_LABELS,
  getAgentStatus,
  getOpenOffboardingRecord as getOpenOffboardingRecordFor
} from './agentStatus';
import { getAgentFlags } from './agentFlags';
import OffboardingModal from 'tailwind-components/Offboarding/OffboardingModal';

const company = getCompany();
const isOneill = company === 'oneill';

const headerCellClass = 'flex h-12 min-h-12 items-center';
const headerCellCenterClass =
  'flex h-12 min-h-12 w-full items-center justify-center';

function TextCell({ children }) {
  return <span className='text-[13px] text-grey'>{children}</span>;
}

TextCell.propTypes = {
  children: propTypes.node
};

function StatusBadge({ status, overrideLabel = null }) {
  const base =
    'flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium w-fit';

  const styles = {
    hold: 'bg-[var(--color-semantic-warning)] text-[var(--color-warning-contrast)]',
    holdCompliance: 'bg-orange-100 text-orange-900',
    offboarding: 'bg-purple-100 text-purple-600',
    active: 'bg-green-100 text-green-700',
    inactive: 'bg-gray-200 text-gray-500'
  };

  const label = AGENT_STATUS_LABELS;

  const dotColors = {
    hold: 'bg-[var(--color-pending)]',
    holdCompliance: 'bg-orange-500',
    offboarding: 'bg-purple-500',
    active: 'bg-green-500',
    inactive: 'bg-gray-400'
  };

  return (
    <div className={`${base} ${styles[status]}`}>
      <span className={`w-2 h-2 rounded-full ${dotColors[status]}`} />
      {overrideLabel || label[status]}
    </div>
  );
}

StatusBadge.propTypes = {
  status: propTypes.string.isRequired,
  overrideLabel: propTypes.string
};

function AgentsTable({
  agents = [],
  openOffboardingAgencies = {},
  openOffboardingLoading = false,
  onOffboardingSuccess
}) {
  const zohoData = useSelector(state => state.user.zohoData);
  const { targetId: viewAsAgent, requesterId: mainZohoId } = useViewAs();
  const userAgencyData = useSelector(state => state.user.zohoData.agencyData);
  const currentUserId = zohoData?.id;
  const userAgencyId = zohoData?.Account_Name?.id;
  const { hidden } = useHiddenMode();

  const [isOpenOffboardModal, setIsOpenOffboardModal] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState(null);

  const hideReleaseApprovalQuestion =
    company === 'sunshine'
      ? true
      : !userAgencyData?.Upline_Allows_Showing_RAQ_On_Offboarding_Survey &&
        !userAgencyData?.Override_Upline_RAQ_Offboarding_Survey_Setting;

  const getOpenOffboardingRecord = useCallback(
    agent => getOpenOffboardingRecordFor(openOffboardingAgencies, agent),
    [openOffboardingAgencies]
  );

  const handleOpenOffboardModal = useCallback(agentId => {
    setSelectedAgentId(agentId);
    setIsOpenOffboardModal(true);
  }, []);

  const handleCloseOffboardModal = useCallback(() => {
    setIsOpenOffboardModal(false);
    setSelectedAgentId(null);
  }, []);

  const handleViewAsAgent = useCallback(agentID => {
    if (isMobileWebView()) {
      postMobileViewAsAgent(agentID);
      return;
    }
    window.open(`/home?va=${agentID}`, '_blank');
  }, []);

  const columns = useMemo(
    () => [
      {
        header: 'View As',
        accessorKey: 'viewasagent',
        size: 120,
        muiTableHeadCellProps: { 'data-tour': 'agency-view-col' },
        Cell: cellProps => {
          const target = cellProps.row.original;
          const isSelfTarget = String(target.id) === String(currentUserId);
          const disabledReason = getViewAsDisabledReason({
            requesterZohoData: zohoData,
            isSelfTarget
          });
          return (
            <span title={disabledReason || undefined}>
              <Button
                disabled={Boolean(disabledReason)}
                size='xsmall'
                variant='secondary'
                text='View'
                icon={<Eye />}
                onClick={() => handleViewAsAgent(target.id)}
              />
            </span>
          );
        }
      },
      {
        header: 'Agent',
        accessorKey: 'First_Name',
        size: 260,
        Cell: cellProps => (
          <div className='flex flex-col'>
            <span className='text-sm font-medium text-black'>
              {cellProps.row.original.First_Name}{' '}
              {cellProps.row.original.Last_Name}
            </span>
            <span className='text-xs text-grey'>
              {maskValue(cellProps.row.original.Email, hidden)}
            </span>
          </div>
        )
      },
      {
        header: 'NPN',
        accessorKey: 'NPN',
        size: 120,
        Cell: cellProps => <TextCell>{cellProps.row.original.NPN}</TextCell>
      },
      {
        header: 'Position',
        accessorKey: 'Position_at_Agency',
        size: 120,
        Cell: cellProps => (
          <TextCell>
            {cellProps.row.original.Position_at_Agency || 'Agent'}
          </TextCell>
        )
      },
      {
        header: 'Status',
        accessorKey: 'status',
        size: 160,
        Cell: cellProps => {
          const agentData = cellProps.row.original;
          const status = getAgentStatus(
            agentData,
            getOpenOffboardingRecord(agentData)
          );
          const overrideLabel = isHoldRank(agentData.rank)
            ? agentData.rank
            : null;
          return (
            <div className='flex justify-center'>
              <StatusBadge status={status} overrideLabel={overrideLabel} />
            </div>
          );
        }
      },
      {
        header: 'Agency',
        accessorKey: 'Agency',
        size: 220,
        Cell: cellProps => (
          <TextCell>{cellProps.row.original.Agency || '—'}</TextCell>
        )
      },
      {
        header: 'Flags',
        accessorKey: 'flags',
        size: 200,
        Cell: cellProps => {
          const flags = getAgentFlags(cellProps.row.original);
          if (flags.length === 0) {
            return <TextCell>—</TextCell>;
          }
          return (
            <div className='flex flex-wrap gap-1 py-0.5'>
              {flags.map(flag => (
                <FlagPill key={flag} text={flag} />
              ))}
            </div>
          );
        }
      },
      ...(isOneill && !viewAsAgent
        ? [
            {
              header: 'Offboarding',
              accessorKey: 'offboardingCell',
              size: 148,
              muiTableHeadCellProps: { 'data-tour': 'agency-offboard-col' },
              Cell: cellProps => {
                const agent = cellProps.row.original;
                const isSelf = isSelfAgent({
                  agentId: agent.id,
                  currentUserId,
                  mainZohoId,
                  viewAsAgent
                });
                const offboardingRecord = getOpenOffboardingRecord(agent);

                if (openOffboardingLoading) {
                  return (
                    <div className='flex justify-center py-1'>
                      <Loader2
                        aria-hidden
                        className='size-6 shrink-0 animate-spin text-grey-dark'
                      />
                    </div>
                  );
                }

                const agentBelongsToMyAgency =
                  String(userAgencyId) === String(agent.Agency_Id);

                if (
                  isSelf ||
                  (offboardingRecord && !offboardingRecord.canOffboard)
                ) {
                  return (
                    <div className='flex flex-col items-center gap-1 py-1 px-2'>
                      <Button
                        size='xsmall'
                        variant='danger'
                        text='Offboard'
                        disabled
                        aria-disabled='true'
                        icon={<CircleX />}
                      />
                      <p className='m-0 max-w-36 text-center text-[10px] leading-tight font-medium text-semantic-danger'>
                        {isSelf
                          ? getCannotOffboardSelfMessage({
                              agentId: agent.id,
                              currentUserId,
                              viewAsAgent
                            })
                          : offboardingRecord.helperText}
                      </p>
                    </div>
                  );
                }

                if (!agentBelongsToMyAgency) {
                  return (
                    <div className='flex flex-col items-center gap-1 py-1 px-2'>
                      <Button
                        size='xsmall'
                        variant='danger'
                        text='Offboard'
                        disabled
                        aria-disabled='true'
                        icon={<CircleX />}
                      />
                      <p className='m-0 max-w-36 text-center text-[10px] leading-tight font-medium text-semantic-danger'>
                        Only admins from {agent.Agency} can offboard this
                        agent.
                      </p>
                    </div>
                  );
                }

                return (
                  <div className='flex flex-col items-center gap-1 py-1 px-2'>
                    <Button
                      size='xsmall'
                      variant='danger'
                      text='Offboard'
                      icon={<CircleX />}
                      onClick={() => handleOpenOffboardModal(agent.id)}
                    />
                    {offboardingRecord?.helperText &&
                      offboardingRecord.canOffboard && (
                        <p className='m-0 max-w-36 text-center text-[10px] leading-tight font-medium text-orange-500'>
                          {offboardingRecord.helperText}
                        </p>
                      )}
                  </div>
                );
              }
            }
          ]
        : [])
    ],
    [
      currentUserId,
      mainZohoId,
      zohoData,
      handleViewAsAgent,
      handleOpenOffboardModal,
      viewAsAgent,
      getOpenOffboardingRecord,
      openOffboardingLoading,
      hidden
    ]
  );

  const data = useMemo(() => [...agents], [agents]);

  const table = useMaterialReactTable({
    columns,
    data,
    enableSorting: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        height: 'fit-content',
        width: '100%',
        boxShadow: 'none'
      }
    },
    muiTableHeadProps: { 'data-tour': 'agency-table-head' },
    muiTableHeadCellProps: headerProps => {
      const accessor = headerProps.column.columnDef.accessorKey;
      const centered = accessor === 'status' || accessor === 'offboardingCell';
      return {
        className: centered ? headerCellCenterClass : headerCellClass,
        ...(centered
          ? {
              align: 'center',
              sx: {
                '& .Mui-TableHeadCell-Content': {
                  justifyContent: 'center'
                }
              }
            }
          : {})
      };
    },
    muiTableBodyCellProps: cellPropsDef => {
      const accessor = cellPropsDef.column.columnDef.accessorKey;
      if (accessor === 'status' || accessor === 'offboardingCell') {
        return { align: 'center' };
      }
      return {};
    },
    muiTableBodyRowProps: rowProps => {
      const status = getAgentStatus(
        rowProps.row.original,
        getOpenOffboardingRecord(rowProps.row.original)
      );
      if (status === 'offboarding') {
        return { sx: { backgroundColor: '#f3f0f7' } };
      }
      return {};
    },
    renderEmptyRowsFallback: () => (
      <p className='m-0 py-6 text-center text-sm font-normal text-grey'>
        No agents found
      </p>
    )
  });

  return (
    <>
      <MaterialReactTable table={table} />
      {isOpenOffboardModal && (
        <OffboardingModal
          source='agencyTable'
          selectedAgentId={selectedAgentId}
          onClose={handleCloseOffboardModal}
          hideReleaseApprovalQuestion={hideReleaseApprovalQuestion}
          onSuccess={onOffboardingSuccess}
        />
      )}
    </>
  );
}

AgentsTable.propTypes = {
  agents: propTypes.arrayOf(propTypes.object),
  openOffboardingAgencies: propTypes.object,
  openOffboardingLoading: propTypes.bool,
  onOffboardingSuccess: propTypes.func
};

export default AgentsTable;
